/**
 * ReviewPage (/review) — production-trace review per the approved mock:
 * main transcript column + right-hand queue sidebar + bottom composer.
 *
 * Queue rows ARE labels rows (flag source = created_by, reason = notes).
 * The trajectory is every trace sharing the flagged trace's
 * conversation_id, fetched with the same fan-out pattern as
 * ConversationTranscript and delta-merged into chat events. Selection
 * lives in the `item` URL param so reviews are linkable. Keyboard:
 * j/k queue nav, ⌘↵ save (handled in the composer).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Inbox } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryKey,
  getGetReviewQueueApiReviewQueueGetQueryKey,
  getGetReviewQueueModelsApiReviewQueueModelsGetQueryKey,
  useGetReviewQueueMetadataApiReviewQueueMetadataGet,
  useGetReviewQueueApiReviewQueueGet,
  useGetReviewQueueModelsApiReviewQueueModelsGet,
  useGetReviewQueuePrStatusApiReviewQueuePrStatusGet,
} from '@/api/generated/review/review'
import type { ReviewQueuePrStatusResponsePrStates } from '@/api/generated/model'
import {
  getGetLabelByTraceApiLabelsByTraceTraceIdGetQueryKey,
  useGetLabelByTraceApiLabelsByTraceTraceIdGet,
} from '@/api/generated/labels/labels'
import { useGetTrajectoryApiTracesTrajectoryTraceKeyGet } from '@/api/generated/traces/traces'
import type { ReviewQueueItem, TraceDocument } from '@/api/generated/model'
import { getApiErrorStatus } from '@/api/client'
import {
  adHocQueueItem,
  existingLabel,
  hydrateAdHocItem,
  isAdHocItem,
} from '@/components/review/adHocItem'
import {
  filterClientMetadata,
  metadataOptions,
  parseMetadataFilter,
  tenantNames,
  withMetadataFilterParam,
  type ClientMetadataFilters,
  type MetadataFilter,
  type QueueClientMetadataIndex,
} from '@/components/review/clientMetadata'
import {
  ANY_MODEL,
  filterModel,
  filterStatus,
  flagHeading,
  flagSourceLabel,
  flagType,
  parseVerdictSummary,
  queueModels,
  rowLean,
  rowVerdict,
  type FlagType,
  type LeanFilter,
  type QueueModelIndex,
  type StatusFilter,
} from '@/components/review/flagSource'
import { matchesQueueSearch } from '@/components/review/queueSearch'
import {
  flaggableRequests,
  flagsOnRun,
  openFlagCounts,
  type FlaggableRequest,
} from '@/components/review/runFlags'
import { RunFlagsBar } from '@/components/review/RunFlagsBar'
import { ReviewChatPanel } from '@/components/review/ReviewChatPanel'
import { ReviewComposer } from '@/components/review/ReviewComposer'
import { TagEditor } from '@/components/trajectories/TagEditor'
import { ReviewQueueSidebar, VerdictChip } from '@/components/review/ReviewQueueSidebar'
import {
  TrajectoryTranscript,
  type TranscriptFlag,
} from '@/components/review/TrajectoryTranscript'
import {
  buildTurns,
  flagAnchorIndex,
  type TrajectoryTurn,
} from '@/components/review/transcript'
import { docToTurn } from '@/components/review/turns'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { useTeam } from '@/contexts/TeamContext'

/** Update the selection and retain its view without clobbering other params. */
function withItemParam(
  prev: URLSearchParams,
  traceId: string,
  status: StatusFilter,
): URLSearchParams {
  const next = new URLSearchParams(prev)
  next.set('item', traceId)
  next.set('status', status)
  return next
}

/** What a stand-in row's header says: no flag source, no reason. */
const AD_HOC_HEADING = 'Not flagged yet'

const METADATA_URL_PARAMS: Record<keyof ClientMetadataFilters, string> = {
  clientTenant: 'client_tenant',
  environment: 'environment',
  trafficType: 'traffic_type',
}

/**
 * One GET /api/traces/trajectory/{key} for the whole run (TRA-905 shape).
 *
 * The per-trace fan-out this replaces paid the browser's per-origin
 * connection cap: a 30-turn run loaded ~6 traces at a time, each with its
 * own API→R2 round trip. The server assembles the same canonical
 * trajectory (`r2_client.fetch_trajectory`) with concurrent reads, and
 * all-or-nothing delivery means a partial trajectory can no longer render
 * as if it were complete.
 */
function useTrajectoryTurns(item: ReviewQueueItem | null): {
  turns: TrajectoryTurn[]
  isLoading: boolean
  // The trajectory is unknown entirely — surfaced as an error and blocks
  // saving (never review blind).
  failed: boolean
  model: string | null
} {
  const query = useGetTrajectoryApiTracesTrajectoryTraceKeyGet(
    item?.trace_key ?? '',
    { query: { enabled: item !== null } },
  )
  const data = query.data
  // Server order is canonical (oldest first) — same order every agent reads.
  const docs: readonly TraceDocument[] = useMemo(
    () => (data?.status === 200 ? data.data : []),
    [data],
  )
  const turns = useMemo(() => docs.map(docToTurn), [docs])

  const arrived = data?.status === 200
  const failed = item !== null && (query.isError || (data !== undefined && !arrived))

  return {
    turns,
    // POSITIVE completeness gate: loading until the trajectory has actually
    // ARRIVED, not merely until nothing is in flight. React Query pauses
    // queries while the browser is offline — paused is pending with
    // isFetching false, and `isLoading` is `isPending && isFetching`, so an
    // isLoading gate reads "loaded" with zero turns and would let a reviewer
    // sign a verdict on a transcript they never saw.
    isLoading: item !== null && !arrived && !failed,
    failed,
    model: docs[0]?.requestModel ?? null,
  }
}

export default function ReviewPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  // Settings (Prompts lives in it) turns restricted demo teams away, so the link is hidden
  // for them rather than left to bounce off the guard (TRA-832).
  const { isRestricted, meSettled } = useTeam()
  const queueQuery = useGetReviewQueueApiReviewQueueGet()
  const queueData = queueQuery.data
  const items: ReviewQueueItem[] = useMemo(
    () => (queueData?.status === 200 ? queueData.data.items : []),
    [queueData],
  )

  // Sidebar filters — client-side over the one already-fetched queue.
  // Selection, j/k, and save-advance all walk the FILTERED list so the
  // keyboard never lands on a hidden row.
  // Free-text search over the row plus both side-channel maps, so a
  // reviewer can find runs by organization, tool (via the flag text),
  // model, or ids (TRA-956).
  const [searchQuery, setSearchQuery] = useState('')
  const [leanFilter, setLeanFilter] = useState<LeanFilter>('all')
  const [typeFilter, setTypeFilter] = useState<ReadonlySet<FlagType>>(new Set())
  // Cartage-1 vs GLM runs get flagged at very different rates, so the queue
  // has to be splittable by model (TRA-907).
  const [modelFilter, setModelFilter] = useState<string>(ANY_MODEL)
  // Keep the view in the URL so automatic item selection and reloads do
  // not turn the daily Unreviewed queue into an explicit All-view link.
  // Older item-only links still open their row in a visible view.
  const requestedStatus = searchParams.get('status')
  const linkedId = searchParams.get('item')
  const statusFilter: StatusFilter =
    requestedStatus === 'all' ||
    requestedStatus === 'unreviewed' ||
    requestedStatus === 'reviewed' ||
    requestedStatus === 'dismissed'
      ? requestedStatus
      : linkedId
        ? items.find((item) => item.trace_id === linkedId)?.review_status ===
          'dismissed'
          ? 'dismissed'
          : 'all'
        : 'unreviewed'

  function setStatusFilter(status: StatusFilter): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('status', status)
        return next
      },
      { replace: true },
    )
  }
  // Models hydrate separately: they cost one R2 body read per trace on the
  // server, so blocking the queue on them made first paint crawl. The stable
  // dropdown stays mounted but disabled until the map arrives (TRA-943).
  const modelsQuery = useGetReviewQueueModelsApiReviewQueueModelsGet()
  const modelsData = modelsQuery.data
  const modelByKey: QueueModelIndex = useMemo(
    () => (modelsData?.status === 200 ? modelsData.data.models : {}),
    [modelsData],
  )
  // A dead request must not render as a dropdown offering only "all models",
  // which reads as "nothing to filter" rather than "fetch broke". Failed
  // means NO usable map: when a background refetch
  // errors, react-query keeps the last good data — a trace's model is
  // immutable, so that map is still correct and the dropdown must stay
  // mounted (hiding it would strand an active filter with no way to clear
  // it). A non-200 envelope empties the map instead, and the reset effect
  // below clears any concrete filter.
  const modelsFailed =
    modelsData === undefined ? modelsQuery.isError : modelsData.status !== 200
  const models = useMemo(
    () => queueModels(items, modelByKey),
    [items, modelByKey],
  )
  // PR number renders straight from pr_url; the open/merged state is the
  // one fact only GitHub has, fetched as its own cheap side-channel map.
  // A failed or tokenless fetch yields an empty map and the buttons
  // degrade to stateless links.
  const prStatusQuery = useGetReviewQueuePrStatusApiReviewQueuePrStatusGet()
  const prStatusData = prStatusQuery.data
  const prStates: ReviewQueuePrStatusResponsePrStates = useMemo(
    () => (prStatusData?.status === 200 ? prStatusData.data.pr_states : {}),
    [prStatusData],
  )
  const metadataQuery = useGetReviewQueueMetadataApiReviewQueueMetadataGet()
  const metadataData = metadataQuery.data
  const metadataByKey: QueueClientMetadataIndex = useMemo(
    () => (metadataData?.status === 200 ? metadataData.data.metadata : {}),
    [metadataData],
  )
  const metadataReady = metadataData?.status === 200
  const metadataFailed =
    metadataData === undefined ? metadataQuery.isError : metadataData.status !== 200
  const clientMetadataFilters: ClientMetadataFilters = useMemo(
    () => ({
      clientTenant: parseMetadataFilter(searchParams.get('client_tenant')),
      environment: parseMetadataFilter(searchParams.get('environment')),
      trafficType: parseMetadataFilter(searchParams.get('traffic_type')),
    }),
    [searchParams],
  )
  const clientMetadataOptions = useMemo(
    () => ({
      clientTenant: metadataOptions(items, 'client_tenant_id', metadataByKey),
      environment: metadataOptions(items, 'environment', metadataByKey),
      trafficType: metadataOptions(items, 'traffic_type', metadataByKey),
    }),
    [items, metadataByKey],
  )
  const clientTenantNames = useMemo(
    () => tenantNames(items, metadataByKey),
    [items, metadataByKey],
  )
  // The ask panel is open from the start (TRA-1081): the reviewer's loop is
  // read the answer, accept or reject, next. Closing it is per visit.
  const [chatOpen, setChatOpen] = useState(true)
  const filteredItems = useMemo(
    () =>
      items.filter(
        (it) =>
          matchesQueueSearch(it, searchQuery, modelByKey, metadataByKey) &&
          filterStatus(it, statusFilter) &&
          (leanFilter === 'all' || rowLean(it) === leanFilter) &&
          (typeFilter.size === 0 || typeFilter.has(flagType(it))) &&
          filterModel(it, modelFilter, modelByKey) &&
          (!metadataReady ||
            filterClientMetadata(it, clientMetadataFilters, metadataByKey)),
      ),
    [
      items,
      searchQuery,
      leanFilter,
      typeFilter,
      modelFilter,
      statusFilter,
      modelByKey,
      metadataReady,
      clientMetadataFilters,
      metadataByKey,
    ],
  )

  // A refetch can retire the selected model (its rows all reviewed, or the
  // trace bodies now readable) — fall back to all rather than showing an
  // empty queue behind a filter value that no longer exists.
  useEffect(() => {
    if (modelFilter !== ANY_MODEL && !models.includes(modelFilter)) {
      setModelFilter(ANY_MODEL)
    }
  }, [models, modelFilter])

  function toggleTypeFilter(type: FlagType): void {
    setTypeFilter((prev) => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  function setClientMetadataFilter(
    field: keyof ClientMetadataFilters,
    filter: MetadataFilter,
  ): void {
    setSearchParams(
      (prev) => withMetadataFilterParam(prev, METADATA_URL_PARAMS[field], filter),
      { replace: true },
    )
  }

  // Selection is keyed by trace_id — virtual eval-flag rows carry no
  // label_id until the reviewer saves, but every row has one trace.
  // The URL-named row resolves against the FULL queue: a filter toggle
  // must never yank the open review (and the composer draft keyed to it)
  // out from under the reviewer. Only the fallbacks walk the filtered
  // list.
  const selectedId = searchParams.get('item')
  const queueLoaded = queueData?.status === 200
  const queueMatch = items.find((it) => it.trace_id === selectedId) ?? null
  // A run opened from the Trajectories page (TRA-1079) may not be in the
  // queue at all — no flag, a held flag, or older than the queue's window.
  // Its link carries the trace key, so the page builds a stand-in row and
  // reviews it anyway; saving creates the label and the refetch swaps the
  // real row in. Built only once the queue has answered, so a real row is
  // never shadowed by the stand-in during the first load.
  // The stand-in is keyed on the KEY, not on `item`: the reviewer can walk
  // the queue with j/k and come back to the pinned row, and a key whose
  // trace is in the queue after all (the save landed, or the run was
  // flagged meanwhile) yields no stand-in at all.
  const adHocKey = searchParams.get('key')
  const standIn: ReviewQueueItem | null = useMemo(() => {
    if (!queueLoaded || adHocKey === null) return null
    const candidate = adHocQueueItem(adHocKey, new Date().toISOString())
    if (candidate === null || items.some((it) => it.trace_id === candidate.trace_id)) {
      return null
    }
    return candidate
  }, [queueLoaded, adHocKey, items])
  // A stand-in run may still carry a label the queue does not show (held,
  // or older than the trace window). Look it up as soon as the stand-in
  // exists and fold it in, so the row shows the stored feedback and the
  // save carries the label's version. A 404 is the common answer and not
  // worth a retry. Always refetched: the label can be edited on the
  // Trajectories page between two visits, and a cached answer would seed
  // the composer with old feedback and a version the save then rejects.
  const labelQuery = useGetLabelByTraceApiLabelsByTraceTraceIdGet(standIn?.trace_id ?? '', {
    query: {
      enabled: standIn !== null,
      staleTime: 0,
      retry: (failureCount, error) =>
        getApiErrorStatus(error) !== 404 && failureCount < 1,
    },
  })
  const {
    data: labelData,
    isFetching: labelIsFetching,
    isError: labelIsError,
    error: labelError,
  } = labelQuery
  const lookup = useMemo(
    () =>
      standIn === null
        ? null
        : existingLabel(labelData, {
            isFetching: labelIsFetching,
            isError: labelIsError,
            status: getApiErrorStatus(labelError),
          }),
    [standIn, labelData, labelIsFetching, labelIsError, labelError],
  )
  const adHoc: ReviewQueueItem | null = useMemo(
    () =>
      standIn !== null &&
      (lookup?.status === 'ready' || lookup?.status === 'refreshing') &&
      lookup.label !== null
        ? hydrateAdHocItem(standIn, lookup.label)
        : standIn,
    [standIn, lookup],
  )
  const adHocMatch = adHoc !== null && adHoc.trace_id === selectedId ? adHoc : null
  // The URL names a run this page cannot show: not in the queue and no
  // usable key (TRA-1168). Say so instead of silently opening some other
  // run — that fallback hid links built from the wrong id.
  const unknownItem =
    queueLoaded && selectedId !== null && queueMatch === null && adHocMatch === null
  const selected: ReviewQueueItem | null = unknownItem
    ? null
    : (queueMatch ??
      adHocMatch ??
      filteredItems.find((it) => it.review_status === 'unreviewed') ??
      filteredItems[0] ??
      null)
  // Two different questions. `selectedIsPinned`: did the selection come
  // from the Trajectories link (stay on it after a save, wait for the label
  // lookup before saving)? `selectedIsAdHoc`: does the row have no flag at
  // all (nothing to pin, no source to name)? A pinned row with a held flag
  // is the first but not the second.
  const selectedIsPinned = adHocMatch !== null
  const selectedIsAdHoc = selected !== null && isAdHocItem(selected)

  // Set `item` without clobbering unrelated params (the object form of
  // setSearchParams replaces the WHOLE query string — it would strip
  // e.g. the dev-only vanity-org preview param). EVERY `item` write goes
  // through this, including the j/k handler.
  function select(traceId: string): void {
    setSearchParams((prev) => withItemParam(prev, traceId, statusFilter), { replace: true })
  }

  // Open one request of the current run to put a new flag on it
  // (TRA-1088). Same URL shape as a Trajectories link: `item` names the
  // trace and `key` lets the page build the stand-in row when the queue
  // does not hold it. Nothing is written until the reviewer saves.
  function openRequest(request: FlaggableRequest): void {
    setSearchParams(
      (prev) => {
        const next = withItemParam(prev, request.traceId, statusFilter)
        next.set('key', request.traceKey)
        return next
      },
      { replace: true },
    )
  }

  // Selection lives in the URL so reviews are linkable — when the URL
  // names no (or a vanished) row and the fallback chain picks one, sync
  // the param so the address always matches what's on screen.
  // Converges in one pass: after the replace, trace_id === selectedId.
  useEffect(() => {
    if (selected !== null && selected.trace_id !== selectedId) {
      select(selected.trace_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- select is stable per render; depending on it would re-run every render
  }, [selected, selectedId])

  // j/k queue navigation — a browser-level key subscription, not data flow.
  // It lives here, not in the queue sidebar, so it keeps working while the
  // queue is folded to its rail (TRA-1085).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'j' && e.key !== 'k') return
      const target = e.target as HTMLElement
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return
      if (filteredItems.length === 0) return
      const currentIndex = selected
        ? filteredItems.findIndex((it) => it.trace_id === selected.trace_id)
        : -1
      const nextIndex =
        e.key === 'j'
          ? Math.min(currentIndex + 1, filteredItems.length - 1)
          : Math.max(currentIndex - 1, 0)
      const next = filteredItems[nextIndex]
      if (next === undefined) return
      setSearchParams((prev) => withItemParam(prev, next.trace_id, statusFilter), {
        replace: true,
      })
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [filteredItems, selected, setSearchParams, statusFilter])

  function handleSaved(): void {
    void queryClient.invalidateQueries({
      queryKey: getGetReviewQueueApiReviewQueueGetQueryKey(),
    })
    // The model map must refresh alongside the queue: a refetched queue can
    // carry rows flagged since page load, and a stale map would hide them
    // behind any concrete model filter. Cheap — the server caches per key.
    void queryClient.invalidateQueries({
      queryKey: getGetReviewQueueModelsApiReviewQueueModelsGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryKey(),
    })
    if (selected === null) return
    // A run opened from the Trajectories page was the point of the visit:
    // stay on it (the refetch swaps the real row in) rather than bouncing
    // the reviewer to whatever the queue holds next. The label lookup is
    // stale now: if the run stays a stand-in (older than the trace window),
    // the next visit must see the version this save wrote.
    if (selectedIsPinned) {
      void queryClient.invalidateQueries({
        queryKey: getGetLabelByTraceApiLabelsByTraceTraceIdGetQueryKey(selected.trace_id),
      })
      return
    }
    // Advance to the next unreviewed item (filtered queue order), skipping
    // the one just saved.
    const next = filteredItems.find(
      (it) => it.review_status === 'unreviewed' && it.trace_id !== selected.trace_id,
    )
    if (next) {
      select(next.trace_id)
    }
  }

  const trajectory = useTrajectoryTurns(selected)
  // Per-request sections: the transcript numbers them (TRA-1080) and the
  // flat event list is their concatenation.
  const sections = useMemo(() => buildTurns(trajectory.turns), [trajectory.turns])
  const events = useMemo(() => sections.flatMap((s) => s.events), [sections])
  // Every flag on the open run (TRA-1088): the queue rows sharing its
  // conversation, placed on their requests. The queue is the source, so a
  // save that adds a flag shows up here on the refetch.
  const requestOf = useMemo(
    () => new Map(sections.map((section) => [section.traceId, section.index])),
    [sections],
  )
  const runFlags = useMemo(
    () => (selected === null ? [] : flagsOnRun(items, selected, requestOf)),
    [items, selected, requestOf],
  )
  const transcriptFlags: TranscriptFlag[] = useMemo(
    () =>
      runFlags
        .filter((flag) => !isAdHocItem(flag.item))
        .map((flag) => ({
          traceId: flag.item.trace_id,
          source: flagHeading(flag.item.created_by, flag.item.notes),
          // The verdict already renders as a chip everywhere; the popover
          // repeats only the human-readable reason.
          reason: parseVerdictSummary(flag.item.notes).text,
          current: selected !== null && flag.item.trace_id === selected.trace_id,
        })),
    [runFlags, selected],
  )
  const flaggable = useMemo(
    () =>
      flaggableRequests(
        trajectory.turns,
        new Set(runFlags.map((flag) => flag.item.trace_id)),
      ),
    [trajectory.turns, runFlags],
  )
  const flagCounts = useMemo(() => openFlagCounts(items), [items])
  // The request a clicked "request N" citation points at. The nonce makes
  // a repeat click on the same number scroll again. Reset per queue item:
  // request 7 of the last run means nothing on this one.
  const [activeRequest, setActiveRequest] = useState<{ n: number; nonce: number } | null>(
    null,
  )
  useEffect(() => {
    setActiveRequest(null)
  }, [selected?.trace_id])
  const jumpToRequest = useCallback((n: number) => {
    setActiveRequest((prev) => ({ n, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [])
  // The button's state must match reality: while the trajectory loads, or
  // when the flagged trace isn't among the rendered events, there is no
  // anchor to scroll to — a live-looking button that does nothing reads as
  // a broken page.
  // A stand-in row has no flag, so nothing is pinned (`''` matches no
  // trace) and "Go to flag" stays off.
  const flaggedTraceId = selected === null || selectedIsAdHoc ? '' : selected.trace_id
  const flagAnchored =
    selected !== null &&
    !trajectory.isLoading &&
    flagAnchorIndex(events, flaggedTraceId) !== null

  function scrollToFlag(): void {
    const anchor = document.getElementById('review-flag-anchor')
    if (anchor === null) {
      // Unreachable while the button is disabled without an anchor; kept as
      // a loud backstop if the two ever disagree.
      console.warn('Go to flag: no flag anchor rendered for this trajectory')
      return
    }
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    <div className="flex h-full [--radius:6px]">
      <div className="flex min-w-0 flex-1 flex-col">
        {selected !== null && (
          <header className="flex items-start gap-8 border-b border-border px-8 py-3.5">
            {/* items-start pins the utilities to the TITLE line: title and
                actions share the top row, the meta line owns the second.
                Centering floated the cluster diagonally into the title's
                corner whenever the column narrowed. */}
            {/* flex-1 min-w-0 is what actually lets the title block
                shrink: with basis auto it refuses to give up its content
                width and the utilities clip out of the column instead. */}
            <div className="min-w-0 flex-1">
              {/* The title names WHAT KIND of item this is; the feedback
                  sentence can run long and lives in the flag popover (and
                  the queue row), not here. */}
              <h2 className="truncate font-serif text-lg text-foreground">
                {selectedIsAdHoc
                  ? AD_HOC_HEADING
                  : flagHeading(selected.created_by, selected.notes)}
              </h2>
              {/* min-w-0 + truncate keep this one line when the ask panel
                  narrows the column — never wrapped under the utilities. */}
              <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                {(() => {
                  const verdict = rowVerdict(selected)
                  return verdict !== null && <VerdictChip {...verdict} />
                })()}
                <span className="truncate">
                  {trajectory.model ?? '—'} · {trajectory.turns.length || '—'} turns ·{' '}
                  {selectedIsAdHoc ? 'no flag' : flagSourceLabel(selected)}
                </span>
                {/* Tags save immediately through their own endpoint — they
                    are organization, not part of the verdict, so they don't
                    wait for Save & next. Keyed per item so the editor's
                    working copy resets when the queue selection moves. */}
                {/* A stand-in row (TRA-1079) does not know the run's stored
                    tags — an editor seeded from [] would wipe them on the
                    first edit. Tags stay editable on the Trajectories page. */}
                {!selectedIsAdHoc && (
                  <span className="ml-2 flex-none">
                    <TagEditor
                      key={selected.trace_id}
                      traceId={selected.trace_id}
                      traceKey={selected.trace_key}
                      tags={selected.tags}
                      // The header is a summary line: three chips, the rest
                      // roll into the +N popover trigger.
                      maxVisible={3}
                    />
                  </span>
                )}
              </div>
            </div>
            {/* Utilities, not actions: the page's primary action is the
                composer below, so this whole cluster stays one quiet text
                tier — the serif title is the only loud thing up here. */}
            {/* pt-1.5 optically centers the mono labels on the serif
                title's cap height. */}
            <div className="ml-auto flex flex-none items-center gap-3 pt-1.5">
              <button
                type="button"
                onClick={scrollToFlag}
                disabled={!flagAnchored}
                title={
                  flagAnchored
                    ? undefined
                    : selectedIsAdHoc
                      ? 'This run has no flag yet'
                      : 'The flagged request is not in the rendered transcript'
                }
                className="flex min-h-8 flex-none items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground"
              >
                <span aria-hidden="true" className="text-warning">
                  ⚑
                </span>
                Go to flag
              </button>
              <span aria-hidden="true" className="h-3 w-px bg-border" />
              {/* Reading a long trajectory to answer one question ("did it
                  do all 108?") is the slow part of triage. Gated on the
                  same load as the composer: an answer about a trajectory
                  that has not arrived would be grounded in nothing. */}
              <button
                type="button"
                aria-pressed={chatOpen}
                onClick={() => {
                  setChatOpen((prev) => !prev)
                }}
                disabled={trajectory.isLoading || trajectory.failed}
                title={
                  trajectory.isLoading || trajectory.failed
                    ? 'The trajectory has not loaded yet'
                    : 'Ask questions about this trajectory'
                }
                className={`flex min-h-8 flex-none items-center rounded-md px-2 text-sm motion-safe:transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  chatOpen
                    ? 'text-foreground underline decoration-2 underline-offset-4'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground disabled:hover:text-muted-foreground'
                }`}
              >
                Ask
              </button>
              {!isRestricted && (
                <span aria-hidden="true" className="h-3 w-px bg-border" />
              )}
              {/* The judge decided this row belongs in the queue, so the
                  fix for a wrong call is the judge's prompt, not the row.
                  Hands the trace over prefilled so the dry run reproduces
                  exactly what the reviewer is looking at. */}
              {!isRestricted && (
                <Link
                  to={`/settings/prompts?trace=${encodeURIComponent(selected.trace_id)}`}
                  title="Tune the dream judge's prompt against this trajectory"
                  className="flex min-h-8 flex-none items-center rounded-md px-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  Fix prompt
                </Link>
              )}
            </div>
          </header>
        )}

        {selected !== null && !trajectory.isLoading && !trajectory.failed && (
          <RunFlagsBar
            flags={runFlags}
            selectedTraceId={selected.trace_id}
            onSelect={select}
            requests={flaggable}
            onFlagRequest={openRequest}
          />
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {queueQuery.isLoading || trajectory.isLoading ? (
            // The trajectory fan-out reads every trace body from R2, which
            // on a long agent run genuinely takes a while on first load —
            // say so instead of showing shapeless skeletons.
            <LoadingPanel label="Loading Trajectory (could take a minute)" />
          ) : queueQuery.isError ? (
            <p className="px-8 py-7 text-sm text-destructive">
              Failed to load the review queue. Refresh to retry.
            </p>
          ) : unknownItem ? (
            <div className="px-8 py-7">
              <h2 className="font-serif text-lg text-foreground">
                No review with that id
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Nothing in the queue has trace id{' '}
                <code className="font-mono">{selectedId}</code>. The link may be stale or
                built from a label id. Pick a run from the queue, or open it from the
                Trajectories page.
              </p>
            </div>
          ) : selected === null ? (
            items.length > 0 ? (
              // The queue has items — the filters just hide all of them.
              // Claiming "nothing to review" here would send the reviewer
              // home with a full queue.
              <div className="px-8 py-7">
                <h2 className="font-serif text-lg text-foreground">
                  No matching items
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  No queue items match the active filters. Clear them in the
                  sidebar to see the rest of the queue.
                </p>
              </div>
            ) : (
              <div className="flex h-full min-h-80 flex-col items-center justify-center px-8 py-12 text-center">
                <Inbox aria-hidden="true" strokeWidth={1.25} className="mb-4 h-8 w-8 text-muted-foreground/70" />
                <h2 className="font-serif text-xl text-foreground/80">
                  Nothing to review
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Flagged production traces will appear here.
                </p>
              </div>
            )
          ) : trajectory.failed ? (
            <p className="px-8 py-7 text-sm text-destructive">
              Failed to load this conversation's traces. Refresh to retry. Saving is
              disabled until the trajectory loads.
            </p>
          ) : (
            <TrajectoryTranscript
              turns={sections}
              flags={transcriptFlags}
              activeRequest={activeRequest}
            />
          )}
        </div>

        {selectedIsPinned && lookup?.status === 'failed' && (
          <p className="px-8 py-3 text-sm text-destructive">
            Failed to load this run's existing review. Refresh to retry. Saving is
            disabled until it loads.
          </p>
        )}
        {/* The pinned composer waits for a FRESH label lookup: it takes
            notes, verdict and version once, at mount, so mounting it on a
            blank or cached row would seed stale feedback over the stored
            review and save with a version the server rejects. */}
        {selected !== null && (!selectedIsPinned || lookup?.status === 'ready') && (
          <ReviewComposer
            key={selected.trace_id}
            item={selected}
            expectedUpdatedAt={selected.label_id === null ? null : selected.updated_at}
            canSave={!trajectory.failed && !trajectory.isLoading}
            onSaved={handleSaved}
          />
        )}
      </div>

      {/* Keyed by the trace so the thread is per queue item and is dropped
          on navigation — questions about the last run are noise on this one.
          Unmounting while the trajectory reloads would also strand an
          in-flight answer against a transcript no longer on screen.
          Held while /me is in flight: isRestricted defaults to false until
          then, and the panel decides on mount whether to spend a model call. */}
      {chatOpen &&
        meSettled &&
        selected !== null &&
        !trajectory.isLoading &&
        !trajectory.failed && (
        <ReviewChatPanel
          key={selected.trace_id}
          item={selected}
          onClose={() => {
            setChatOpen(false)
          }}
          requestCount={sections.length}
          onRequestClick={jumpToRequest}
        />
      )}

      <ReviewQueueSidebar
        items={filteredItems}
        pinned={adHoc}
        flagCounts={flagCounts}
        totalWaiting={items.filter((it) => it.review_status === 'unreviewed').length}
        isLoading={queueQuery.isLoading}
        selectedTraceId={selected?.trace_id ?? null}
        onSelect={select}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        prStates={prStates}
        leanFilter={leanFilter}
        onLeanFilterChange={setLeanFilter}
        typeFilter={typeFilter}
        onToggleTypeFilter={toggleTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        models={models}
        modelsFailed={modelsFailed}
        modelsLoading={modelsQuery.isPending}
        modelFilter={modelFilter}
        onModelFilterChange={setModelFilter}
        clientMetadata={{
          filters: clientMetadataFilters,
          options: clientMetadataOptions,
          tenantNames: clientTenantNames,
          loading: metadataQuery.isPending,
          failed: metadataFailed,
          onChange: setClientMetadataFilter,
        }}
      />
    </div>
  )
}
